import PermissionButton from '../../permissions/PermissionButton';
import { usePermissions } from '../../permissions/usePermissions';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Table, Input, Button, Space, Card, Tooltip, message, Select, Tag, Drawer } from 'antd';
import { SearchOutlined, EnvironmentOutlined, PlusOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { useFetchAllArticles } from '../../hooks/articleHooks';
import { useFetchAllEntrepots } from '../../hooks/entrepotHooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArticleForm from './ArticleForm';
import ArticleDBFImportModal from './ArticleDBFImportModal';

const ArticleList = ({ onSelectArticle, onChangeView, externalQuery }) => {
  const { consultation, zoning } = usePermissions('articles');
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize pagination from URL or default
  const getInitialPagination = () => {
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 10;
    return { current: page, pageSize };
  };

  // Search states
  const [articleSearch, setArticleSearch] = useState('');
  const [codeArtSearch, setCodeArtSearch] = useState('');
  const [selectedEntrepots, setSelectedEntrepots] = useState(['all']); // Default to 'all'
  const [pagination, setPagination] = useState(getInitialPagination);
  const [sorter, setSorter] = useState({
    field: 'code_art',
    order: 'ascend'
  });
  
  const searchInput = useRef(null);
  
  // Fetch entrepots
  const { entrepots } = useFetchAllEntrepots();

  // Sync pagination state to URL when pagination changes
  useEffect(() => {
    const currentPageInUrl = parseInt(searchParams.get('page')) || 1;
    const currentPageSizeInUrl = parseInt(searchParams.get('pageSize')) || 10;
    
    // Only update URL if values have changed
    if (currentPageInUrl !== pagination.current || currentPageSizeInUrl !== pagination.pageSize) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (pagination.current > 1) {
        newSearchParams.set('page', pagination.current.toString());
      } else {
        newSearchParams.delete('page');
      }
      if (pagination.pageSize !== 10) {
        newSearchParams.set('pageSize', pagination.pageSize.toString());
      } else {
        newSearchParams.delete('pageSize');
      }
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  // Sync URL to pagination state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page')) || 1;
    const pageSizeFromUrl = parseInt(searchParams.get('pageSize')) || 10;
    
    if (pageFromUrl !== pagination.current || pageSizeFromUrl !== pagination.pageSize) {
      setPagination({
        current: pageFromUrl,
        pageSize: pageSizeFromUrl
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Combine all params for the API call
  const params = useMemo(() => {
    // Prepare entrepot filter - exclude 'all' and convert to array
    const entrepotFilterArray = selectedEntrepots.includes('all') || selectedEntrepots.length === 0
      ? null 
      : selectedEntrepots.filter(e => e !== 'all' && e != null && e !== '');
    
    const baseParams = {
      page: pagination.current,
      pageSize: pagination.pageSize,
      // If externalQuery is provided, apply it only to code search
      articleSearch,
      codeArtSearch: externalQuery ?? codeArtSearch,
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    };
    
    // Add entrepotFilter as JSON string if we have selected entrepots (not 'all')
    if (entrepotFilterArray && entrepotFilterArray.length > 0) {
      baseParams.entrepotFilter = JSON.stringify(entrepotFilterArray);
      console.log('[ArticleList] Sending entrepotFilter:', baseParams.entrepotFilter);
    } else {
      console.log('[ArticleList] No entrepotFilter - showing all articles');
    }
    
    return baseParams;
  }, [pagination.current, pagination.pageSize, articleSearch, codeArtSearch, sorter.field, sorter.order, externalQuery, selectedEntrepots]);

  const { articles, total, loading, error, refetch } = useFetchAllArticles(params);
  const navigate = useNavigate();

  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [dbfImportModalOpen, setDbfImportModalOpen] = useState(false);

  // Auto-open the DBF import modal when navigated with ?importDbf=1
  // (e.g. from the achat reception import when articles are missing)
  useEffect(() => {
    if (searchParams.get('importDbf') === '1') {
      setDbfImportModalOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('importDbf');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleArticleCreated = () => {
    setCreateDrawerOpen(false);
    refetch && refetch();
  };

  // Search and filtering function
  const getColumnSearchProps = (dataIndex, placeholder, setState) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={placeholder || `Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex, setState)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex, setState)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button 
            onClick={() => handleReset(clearFilters, dataIndex, setState)} 
            size="small" 
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current && searchInput.current.select());
      }
    },
    render: text => text,
  });

  // Handling search and reset
  const handleSearch = (selectedKeys, confirm, dataIndex, setState) => {
    confirm();
    setState(selectedKeys[0] || '');
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page on new search
  };

  const handleReset = (clearFilters, dataIndex, setState) => {
    clearFilters();
    setState('');
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page on filter clear
  };

  const arraysEqual = (arr1 = [], arr2 = []) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every(item => arr2.includes(item));
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    if (sorter.field && sorter.order) {
      setSorter({
        field: sorter.field,
        order: sorter.order
      });
    }

    let shouldResetPage = false;

    if (Object.prototype.hasOwnProperty.call(filters, 'stock')) {
      const stockFiltersRaw = filters.stock;
      const normalizedStockFilters = Array.isArray(stockFiltersRaw)
        ? stockFiltersRaw.filter(e => e != null && e !== '')
        : [];

      const currentSelections = selectedEntrepots.includes('all')
        ? []
        : selectedEntrepots;

      if (!stockFiltersRaw || normalizedStockFilters.length === 0) {
        if (!(selectedEntrepots.length === 1 && selectedEntrepots[0] === 'all')) {
          setSelectedEntrepots(['all']);
          shouldResetPage = true;
        }
      } else if (!arraysEqual(normalizedStockFilters, currentSelections)) {
        setSelectedEntrepots(normalizedStockFilters);
        shouldResetPage = true;
      }
    }

    if (shouldResetPage) {
      setPagination(prev => ({
        ...prev,
        current: 1,
        pageSize: newPagination.pageSize,
      }));
    } else {
      setPagination({
        current: newPagination.current,
        pageSize: newPagination.pageSize,
      });
    }
  };

  if (error) return <p>Error: {error}</p>;

  const columns = [
    {
      title: "Code Article",
      dataIndex: "code_art",
      key: "code_art",
      sorter: true,
      sortOrder: sorter.field === 'code_art' ? sorter.order : null,
      ...getColumnSearchProps("code_art", "Search code", setCodeArtSearch),
    },
    {
      title: "Article",
      dataIndex: "ARTICLE",
      key: "ARTICLE",
      sorter: true,
      sortOrder: sorter.field === 'ARTICLE' ? sorter.order : null,
      ...getColumnSearchProps("ARTICLE", "Search article name", setArticleSearch),
    },
    {
      title: "Stock",
      key: "stock",
      width: 200,
      filters: entrepots.map(entrepot => ({
        text: `${entrepot.code_ent} - ${entrepot.name}`,
        value: entrepot.code_ent,
      })),
      filteredValue: selectedEntrepots.includes('all') ? null : selectedEntrepots,
      onFilter: (value, record) => {
        // This is for client-side filtering display, but we handle server-side filtering via params
        if (!record.stocks || record.stocks.length === 0) return false;
        return record.stocks.some(s => s.code_ent === value && s.stock !== null);
      },
      filterSearch: true,
      render: (_, record) => {
        if (!record.stocks || record.stocks.length === 0) {
          return <Tag color="default">Pas de stock</Tag>;
        }
        
        const showAll = selectedEntrepots.includes('all');
        const stocksToShow = showAll 
          ? record.stocks 
          : record.stocks.filter(s => selectedEntrepots.includes(s.code_ent));
        
        // Show all stocks that exist in the table (including 0 and negative)
        const existingStocks = stocksToShow.filter(s => s.stock !== null);
        
        if (existingStocks.length === 0) {
          // Check if there are any entrepots that don't have stock records
          const noStockRecords = stocksToShow.filter(s => s.stock === null);
          if (noStockRecords.length > 0) {
            return <Tag color="default">Pas de stock</Tag>;
          }
          return <Tag color="default">Pas de stock</Tag>;
        }
        
        return (
          <Space size={[4, 4]} wrap>
            {existingStocks.map((entrepotStock) => {
              const qty = entrepotStock.stock.qte_stk;
              let tagColor = 'blue';
              if (qty < 0) {
                tagColor = 'red';
              } else if (qty === 0) {
                tagColor = 'orange';
              }
              
              return (
                <Tag 
                  key={entrepotStock.code_ent} 
                  color={tagColor}
                  title={`${entrepotStock.name} (${entrepotStock.code_ent}): ${qty}`}
                >
                  {entrepotStock.name} ({entrepotStock.code_ent}): {qty}
                </Tag>
              );
            })}
            {/* Show if there are entrepots without stock records */}
            {stocksToShow.filter(s => s.stock === null).length > 0 && (
              <Tag color="default">N/A</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Prix Achat (ttc)",
      dataIndex: "PA_TTC",
      key: "PA_TTC",
      sorter: true,
      sortOrder: sorter.field === 'PA_TTC' ? sorter.order : null,
    },
    {
      title: "Prix Vente (ttc)",
      dataIndex: "PV_TTC",
      key: "PV_TTC",
      sorter: true,
      sortOrder: sorter.field === 'PV_TTC' ? sorter.order : null,
    },
  ].filter(column => !consultation || !['PA_HT', 'PA_TTC'].includes(column.dataIndex));

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
    marginTop: "-10px",
  };
  
  const tableCardStyle = {
    marginTop: "20px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  return (
    <div>
      <Card style={tableCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ ...titleStyle, margin: 0, border: 'none', padding: 0 }}>Articles List</h2>
          <Space wrap>
            <PermissionButton permissionModule="articles" permissionAction="create" type="primary" icon={<PlusOutlined />} onClick={() => setCreateDrawerOpen(true)}>
              Nouvel article
            </PermissionButton>
            <Button icon={<CloudDownloadOutlined />} onClick={() => setDbfImportModalOpen(true)}>
              Importer nouveaux articles (FTP)
            </Button>
            {onChangeView && zoning && (
              <Tooltip title="Manage article zones and floor levels">
                <PermissionButton permissionModule="articles" permissionAction="zoning"
                  type="primary" 
                  icon={<EnvironmentOutlined />}
                  onClick={() => onChangeView('zoning')}
                  style={{ 
                    backgroundColor: '#52c41a', 
                    borderColor: '#52c41a',
                    fontWeight: '500'
                  }}
                  size="large"
                >
                   Zoning
                </PermissionButton>
              </Tooltip>
            )}
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={articles}
          rowKey="code_art"
          loading={loading}
          pagination={{ 
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          size="small"
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => onSelectArticle(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Drawer
        title="Ajouter un article"
        placement="right"
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        width={1000}
        destroyOnClose
        bodyStyle={{ padding: 0, background: '#fafafa' }}
      >
        <div style={{ padding: 24, minHeight: '100%' }}>
          <ArticleForm onSuccess={handleArticleCreated} />
        </div>
      </Drawer>

      <ArticleDBFImportModal
        visible={dbfImportModalOpen}
        onClose={() => setDbfImportModalOpen(false)}
        onImported={() => refetch && refetch()}
      />
    </div>
  );
};

export default ArticleList;
